/**
 * Rider Edit Page
 * 
 * Page for editing an existing rider with:
 * - RiderForm component pre-populated with rider data
 * - Group data fetching
 * - Delete confirmation modal
 * - Navigation after update/delete
 * 
 * Requirements: 4.6, 4.7
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useRidersStore from '../store/ridersStore';
import { useAuth } from '../auth/AuthProvider';
import { restClient, CreateRiderDto, UpdateRiderDto, Group } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Modal } from '../components/common/Modal';
import { RiderForm } from '../components/features/riders/RiderForm';
import { formatRiderName } from '../utils/riderDisplay';

/**
 * Parse associated users from JSON string
 */
function parseAssociatedUsers(associatedUsersJson?: string): Array<{ user_id: string; role: string }> {
  if (!associatedUsersJson) {
    return [];
  }

  try {
    const parsed = JSON.parse(associatedUsersJson);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch (error) {
    return [];
  }
}

/**
 * Rider Edit Page Component
 */
export function RiderEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    selectedRider,
    loading: riderLoading,
    error: riderError,
    selectRider,
    updateRider,
    deleteRider,
    clearSelectedRider,
  } = useRidersStore();

  // Get auth state for permission checks
  const { isAdmin, user } = useAuth();
  
  // Check if current user is associated with this rider
  const isAssociatedUser = useMemo(() => {
    if (!selectedRider || !user) return false;
    
    const associatedUsers = parseAssociatedUsers(selectedRider.associatedUsers);
    return associatedUsers.some(assoc => 
      assoc.user_id === user.email || 
      assoc.user_id === user.username
    );
  }, [selectedRider, user]);
  
  // User can edit if they're admin or associated with the rider (coaches can only view)
  const canEdit = isAdmin || isAssociatedUser;

  // Local state
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFinalDeleteModal, setShowFinalDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Fetch rider and groups
  useEffect(() => {
    if (id) {
      selectRider(id);
    }

    const fetchData = async () => {
      try {
        const fetchedGroups = await restClient.getGroups();
        setGroups(fetchedGroups);
        setGroupsLoading(false);
      } catch (err) {
        setGroupsError('Failed to load form data. Please try again.');
        setGroupsLoading(false);
      }
    };

    fetchData();

    return () => {
      clearSelectedRider();
    };
  }, [id, selectRider, clearSelectedRider]);

  /**
   * Handle form submission
   * Requirements: 4.6
   */
  const handleSubmit = useCallback(async (data: CreateRiderDto | UpdateRiderDto) => {
    if (!selectedRider) return;

    try {
      await updateRider(selectedRider.rider_id, data as UpdateRiderDto);
      // Navigate to the riders list
      navigate('/riders');
    } catch (error) {
      // Error is handled by the form component
      throw error;
    }
  }, [selectedRider, updateRider, navigate]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (selectedRider) {
      navigate(`/riders/${selectedRider.rider_id}`);
    } else {
      navigate('/riders');
    }
  }, [selectedRider, navigate]);

  /**
   * Handle delete - first confirmation
   */
  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  /**
   * Handle first confirm → show second (final) confirmation
   */
  const handleDeleteFirstConfirm = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteConfirmName('');
    setShowFinalDeleteModal(true);
  }, []);

  /**
   * Handle final delete confirmation
   * Requirements: 4.7
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedRider) return;

    setDeleting(true);
    try {
      await deleteRider(selectedRider.rider_id);
      navigate('/riders');
    } catch (error) {
      setDeleting(false);
      setShowFinalDeleteModal(false);
    }
  }, [selectedRider, deleteRider, navigate]);

  /**
   * Handle delete cancel
   */
  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setShowFinalDeleteModal(false);
    setDeleteConfirmName('');
  }, []);

  const loading = riderLoading || groupsLoading;
  const error = riderError || groupsError;

  if (loading && !selectedRider) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <Button variant="primary" onClick={handleCancel}>
                Back to Riders
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!selectedRider) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900">Rider not found</h3>
            <div className="mt-6">
              <Button variant="primary" onClick={handleCancel}>
                Back to Riders
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Check if user has permission to edit this rider
  if (!canEdit) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">You don't have permission to edit this rider</p>
            <div className="mt-6">
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        {/* Back button - full width on mobile */}
        <div className="mb-4">
          <Button variant="secondary" onClick={handleCancel}>
            ← Back
          </Button>
        </div>
        
        {/* Rider info and actions - stack on mobile, side-by-side on desktop */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">Edit Rider</h1>
            <p className="text-gray-600 mt-1 break-words">{formatRiderName(selectedRider)}</p>
          </div>

          {/* Only admins can delete riders */}
          {isAdmin && (
            <Button variant="danger" onClick={handleDeleteClick} className="w-full sm:w-auto">
              Delete Rider
            </Button>
          )}
        </div>
      </div>

      {/* Form */}
      <Card>
        <RiderForm
          rider={selectedRider}
          groups={groups}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          restrictedFields={!isAdmin}
          restrictSeasons={!isAdmin}
          hideRaceRegistration={!isAdmin}
          allowedFields={
            isAssociatedUser && !isAdmin 
              ? ['first_name', 'last_name', 'birthday', 'grade']
              : undefined
          }
        />
      </Card>

      {/* Delete Confirmation Modal — Step 1 */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Delete Rider"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <svg className="h-6 w-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-red-800 font-medium">This is a destructive action</p>
          </div>
          <p className="text-gray-700">
            You are about to delete <strong>{formatRiderName(selectedRider)}</strong>.
          </p>
          <p className="text-sm text-gray-600">
            This will permanently remove the rider and all associated data including participation history, time trials, and event records. This action cannot be undone.
          </p>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button variant="secondary" onClick={handleDeleteCancel}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteFirstConfirm}>
              Continue with Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal — Step 2 (Final) */}
      <Modal
        isOpen={showFinalDeleteModal}
        onClose={handleDeleteCancel}
        title="Final Confirmation"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <svg className="h-6 w-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <p className="text-red-800 font-medium">This cannot be undone</p>
          </div>
          <p className="text-gray-700">
            To confirm, type the rider's name: <strong>{selectedRider.name}</strong>
          </p>
          <input
            type="text"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            placeholder="Type rider name to confirm"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            disabled={deleting}
            autoFocus
          />

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button variant="secondary" onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={deleting || deleteConfirmName.trim().toLowerCase() !== selectedRider.name.trim().toLowerCase()}
            >
              {deleting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Permanently Delete Rider'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default RiderEdit;
